/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Textarea, Text, Avatar, Button, DatetimeText } from '@components/atoms'

import { useAppSelector, ApiGetDataStatus, CrudType } from '@redux/index'
import { DashboardMessageType, UserType } from '@schemas'
import { grey, blueGrey } from '@mui/material/colors'
import dayjs from 'dayjs'

interface PropsType {
	status: ApiGetDataStatus
	dataList: DashboardMessageType[]
	onAddClick?: (str: string) => void
	onDeleteClick?: (detailId: string, messageId: string) => void
}

export const DashboardMessage = (props: PropsType) => {
	const { dataList, status } = props

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const user: UserType = useAppSelector(state => state.user.user)

	// state
	const [addMessage, setAddMessage] = React.useState<string>('')

	// const onMessageContentChange = (e: TextAreaChangeEvent) => {
	// 	const str = e.value
	// 	setAddMessage(str)
	// }

	const onSaveClick = () => {
		if (props.onAddClick) {
			props.onAddClick(addMessage)
		}
	}

	const onMessageDeleteClick = (detailId: string, messageId: string) => {
		if (props.onDeleteClick) {
			props.onDeleteClick(detailId, messageId)
		}
	}

	return (
		<View css={style}>
			{['init', 'loading'].includes(status) ? (
				<></>
			) : (
				<View>
					<View className="dashboardInnerView">
						<Text
							category="s2"
							className="dashboardCount"
						>{`댓글 ${dataList.length}`}</Text>
					</View>

					<View className="dashboardView">
						{dataList.map(aData => {
							return (
								<View key={aData.id} className="dashboardContentView">
									<View className="dashboardMainContentView">
										<View className="dashboardAvatarView">
											<Avatar
												className="dashboardAvatar"
												picture={aData.createdByImage}
												name={aData.createdByName}
												color={
													aData.createdByColor === ''
														? blueGrey[100]
														: aData.createdByColor
												}
												extpandingDisable={true}
											/>
										</View>
										<View className="dashboardMainContentInnerView">
											<View className="dashboardMainContentInnerTopView">
												<Text category="c1" className="dashboardCreatedByName">
													{aData.createdByName}
												</Text>

												<DatetimeText
													className="dashboardCreateAt"
													title={dayjs(aData.createdAt.split('.')[0]).format(
														'YYYY-MM-DD HH:mm',
													)}
													category="c1"
													iconStyle={{ fontSize: '14px', marginTop: '0.1rem' }}
												/>
											</View>
											<View>
												<Text category="c1" className="dashboardInnerText">
													{aData.content}
												</Text>
											</View>
										</View>
										{(aData.createdByName === user.name || crud.includes('D')) && (
											<Button
												variant="text"
												color="inherit"
												className="deleteButton"
												onClick={() => onMessageDeleteClick(aData.boardId, aData.id)}
											>
												삭제
											</Button>
										)}
									</View>
								</View>
							)
						})}
					</View>
					<View className="dashboardInputView">
						<View className="dashboardInputContentView">
							<View className="dashboardInputMainContentView">
								<View className="dashboardInputAvatarView">
									<Avatar
										className="dashboardInputAvatar"
										picture={user.profileImageUrl}
										name={user.name}
										color={user.profileColor === '' ? blueGrey[100] : user.profileColor}
										extpandingDisable={true}
									/>
								</View>
								<View className="dashboardInputMainContentInnerView">
									<Textarea
										className="dashboardInputTextarea"
										maxRows={3}
										value={addMessage}
										// onChange={onMessageContentChange}
									/>
									<View>
										<Button
											variant="contained"
											color="inherit"
											className="SubmitButton"
											onClick={onSaveClick}
										>
											등록
										</Button>
										{/* )} */}
									</View>
								</View>
							</View>
						</View>
					</View>
				</View>
			)}
		</View>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	background: grey[200],
	boxSizing: 'border-box',
	margin: '0.5rem 0',

	'.dashboardInnerView': {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		height: '2rem',

		'.dashboardCount': {
			position: 'absolute',
			width: 'fit-content',
			height: 'fit-content',
			top: '0.5rem',
			bottom: 0,
			left: '1rem',
			margin: 'auto',
			color: grey[900],
		},
	},

	'.dashboardView': {
		width: '100%',
		height: 'fit-content',
		marginBottom: '1rem',
		padding: '0 1rem',

		'.dashboardContentView': {
			display: 'flex',
			alignItem: 'center',
			justifyContent: 'space-between',
			width: '100%',
			height: 'fit-content',
			borderBottom: '1px solid',
			borderColor: grey[400],

			'.dashboardMainContentView': {
				display: 'flex',
				width: '100%',
				marginLeft: '1rem',
				textAlign: 'left',

				'.dashboardAvatarView': {
					display: 'flex',
					alignItems: 'flex-start',
					width: 'fit-content',
					paddingTop: '0.6rem',

					'.dashboardAvatar': {
						margin: 0,
					},
				},

				'.dashboardMainContentInnerView': {
					width: '90%',
					display: 'flex',
					flexDirection: 'column',
					marginLeft: '1rem',

					'.dashboardMainContentInnerTopView': {
						display: 'flex',
						flexDirection: 'row',
						marginTop: 0,
						color: grey[700],

						'.dashboardCreatedByName': {
							width: 'fit-content',
							marginRight: '0.5rem',
							paddingRight: '0.5rem',
							borderRight: '1px solid',
							borderColor: grey[400],
						},
					},

					'.dashboardInnerText': {
						width: 'fit-content',
						marginTop: '0.1rem',
						color: grey[700],
					},
				},

				'.deleteButton': {
					display: 'flex',
					alignItems: 'flex-start',
					width: 'fit-content',
					height: 'fit-content',
					color: grey[600],
					fontSize: '12px',
				},
			},
		},
	},

	'.dashboardInputView': {
		width: '100%',
		height: '4rem',
		padding: '0 1rem',

		'.dashboardInputContentView': {
			display: 'flex',
			alignItem: 'center',
			justifyContent: 'space-between',
			width: '100%',
			height: '3rem',

			'.dashboardInputMainContentView': {
				display: 'flex',
				width: '100%',
				marginLeft: '1rem',
				textAlign: 'left',

				'.dashboardInputAvatarView': {
					width: 'fit-content',
					paddingTop: '0.2rem',

					'.dashboardInputAvatar': {
						margin: 0,
					},
				},

				'.dashboardInputMainContentInnerView': {
					display: 'flex',
					width: '100%',
					marginLeft: '1rem',

					'.dashboardInputTextarea': {
						marginRight: '0.5rem',
						textarea: {
							resize: 'none',
							fontSize: '12px',
						},
					},

					'.SubmitButton': {
						width: 'fit-content',
						marginTop: '0.3rem',
					},
				},
			},
		},
	},

	'@media (max-width: 400px)': {
		'.dashboardView': {
			padding: 0,

			'.dashboardContentView': {
				'.dashboardMainContentView': {
					marginLeft: '0.8rem',

					'.dashboardMainContentInnerView': {
						'.dashboardMainContentInnerTopView': {
							display: 'block',
							marginTop: 0,

							'.dashboardCreatedByName': {
								marginBottom: '0.5rem',
								borderRight: '0px',
							},

							'.dashboardCreateAt': {
								margin: '0.5rem 0',
								marginBottom: '1rem',
								paddingLeft: 0,
								borderLeft: 0,
								borderColor: grey[400],

								'&.mobile': {
									display: 'none',
								},
							},
						},
					},
				},
			},
		},

		'.dashboardInputView': {
			padding: 0,

			'.dashboardInputContentView': {
				'.dashboardInputMainContentView': {
					marginLeft: '0.5rem',
					'.dashboardInputMainContentInnerView': {
						marginLeft: '0.5rem',

						'.dashboardInputTextarea': {
							marginRight: '0.5rem',
						},

						'.SubmitButton': {
							marginRight: '0.5rem',
						},
					},
				},
			},
		},
	},
})
